/*
 * Minimal, safe markdown for assistant replies (NEXUS-1384 chat v2).
 *
 * Supports what chat models actually emit: paragraphs, line breaks, **bold**, *italic*,
 * `code`, fenced ``` code blocks, # headings, - / * / 1. lists, > quotes, [links](https://…)
 * and simple | pipe | tables. Never renders raw HTML — everything is React text nodes, and the
 * export variant escapes every character it emits. Links are limited to http(s) and mailto.
 */

import type { ReactNode } from 'react';

type Block =
  | { t: 'p'; text: string }
  | { t: 'h'; level: number; text: string }
  | { t: 'code'; text: string; lang: string }
  | { t: 'ul' | 'ol'; items: string[] }
  | { t: 'quote'; text: string }
  | { t: 'table'; head: string[]; rows: string[][] };

const SAFE_URL = /^(https?:\/\/|mailto:)/i;

function parseBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n?/g, '\n').split('\n');
  const out: Block[] = [];
  let i = 0;
  const isTableRow = (l: string) => /^\s*\|.*\|\s*$/.test(l);
  const cells = (l: string) => l.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) {
      i++;
      continue;
    }
    const fence = /^\s*```\s*([^\s`]*).*$/.exec(line);
    if (fence) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i]!)) body.push(lines[i++]!);
      i++;
      out.push({ t: 'code', text: body.join('\n'), lang: fence[1] ?? '' });
      continue;
    }
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      out.push({ t: 'h', level: h[1]!.length, text: h[2]! });
      i++;
      continue;
    }
    if (isTableRow(line) && i + 1 < lines.length && /^\s*\|?\s*:?-{2,}/.test(lines[i + 1]!)) {
      const head = cells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && isTableRow(lines[i]!)) rows.push(cells(lines[i++]!));
      out.push({ t: 'table', head, rows });
      continue;
    }
    if (/^\s*[-*•]\s+/.test(line) || /^\s*\d+[.)]\s+/.test(line)) {
      const ordered = /^\s*\d+[.)]\s+/.test(line);
      const items: string[] = [];
      while (i < lines.length && (ordered ? /^\s*\d+[.)]\s+/ : /^\s*[-*•]\s+/).test(lines[i]!)) {
        items.push(lines[i++]!.replace(ordered ? /^\s*\d+[.)]\s+/ : /^\s*[-*•]\s+/, ''));
      }
      out.push({ t: ordered ? 'ol' : 'ul', items });
      continue;
    }
    if (/^\s*>\s?/.test(line)) {
      const body: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i]!)) body.push(lines[i++]!.replace(/^\s*>\s?/, ''));
      out.push({ t: 'quote', text: body.join('\n') });
      continue;
    }
    const body: string[] = [];
    while (
      i < lines.length &&
      lines[i]!.trim() &&
      !/^\s*```/.test(lines[i]!) &&
      !/^#{1,4}\s/.test(lines[i]!) &&
      !/^\s*([-*•]|\d+[.)])\s+/.test(lines[i]!) &&
      !/^\s*>/.test(lines[i]!)
    )
      body.push(lines[i++]!);
    // Never stall: a line no rule above consumed still becomes text.
    if (body.length === 0) body.push(lines[i++]!);
    out.push({ t: 'p', text: body.join('\n') });
  }
  return out;
}

type Inline = { t: 'text' | 'b' | 'i' | 'code'; v: string } | { t: 'a'; v: string; href: string } | { t: 'br' };

function parseInline(s: string): Inline[] {
  const out: Inline[] = [];
  const re = /(`[^`]+`)|(\*\*[^*]+\*\*)|(__[^_]+__)|(\*[^*\s][^*]*\*)|(\[[^\]]+\]\([^)\s]+\))|(\n)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s))) {
    if (m.index > last) out.push({ t: 'text', v: s.slice(last, m.index) });
    const tok = m[0];
    if (m[1]) out.push({ t: 'code', v: tok.slice(1, -1) });
    else if (m[2] || m[3]) out.push({ t: 'b', v: tok.slice(2, -2) });
    else if (m[4]) out.push({ t: 'i', v: tok.slice(1, -1) });
    else if (m[5]) {
      const lm = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(tok)!;
      if (SAFE_URL.test(lm[2]!)) out.push({ t: 'a', v: lm[1]!, href: lm[2]! });
      else out.push({ t: 'text', v: lm[1]! });
    } else out.push({ t: 'br' });
    last = m.index + tok.length;
  }
  if (last < s.length) out.push({ t: 'text', v: s.slice(last) });
  return out;
}

function inlineNodes(s: string): ReactNode[] {
  return parseInline(s).map((n, i) => {
    switch (n.t) {
      case 'b':
        return <strong key={i}>{n.v}</strong>;
      case 'i':
        return <em key={i}>{n.v}</em>;
      case 'code':
        return (
          <code key={i} className="px-1 rounded" style={{ background: 'rgba(16,28,66,0.06)', fontSize: '0.88em' }}>
            {n.v}
          </code>
        );
      case 'a':
        return (
          <a key={i} href={n.href} target="_blank" rel="noopener noreferrer">
            {n.v}
          </a>
        );
      case 'br':
        return <br key={i} />;
      default:
        return <span key={i}>{n.v}</span>;
    }
  });
}

export function Markdown({ text }: { text: string }) {
  const blocks = parseBlocks(text);
  return (
    <div className="aqua-md">
      {blocks.map((b, i) => {
        switch (b.t) {
          case 'h': {
            const size = b.level <= 2 ? '1.05rem' : '0.98rem';
            return (
              <div key={i} className="fw-bold mt-2 mb-1" style={{ fontSize: size }}>
                {inlineNodes(b.text)}
              </div>
            );
          }
          case 'code':
            return (
              <pre key={i} className="p-2 rounded small mb-2" style={{ background: 'rgba(16,28,66,0.06)', whiteSpace: 'pre-wrap' }}>
                <code>{b.text}</code>
              </pre>
            );
          case 'ul':
          case 'ol': {
            const Tag = b.t;
            return (
              <Tag key={i} className="mb-2 ps-4">
                {b.items.map((it, j) => (
                  <li key={j}>{inlineNodes(it)}</li>
                ))}
              </Tag>
            );
          }
          case 'quote':
            return (
              <blockquote key={i} className="border-start border-3 ps-2 text-muted mb-2">
                {inlineNodes(b.text)}
              </blockquote>
            );
          case 'table':
            return (
              <div key={i} className="table-responsive mb-2">
                <table className="table table-sm small mb-0">
                  <thead>
                    <tr>{b.head.map((h, j) => <th key={j}>{inlineNodes(h)}</th>)}</tr>
                  </thead>
                  <tbody>
                    {b.rows.map((r, j) => (
                      <tr key={j}>{r.map((c, k) => <td key={k}>{inlineNodes(c)}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          default:
            return (
              <p key={i} className="mb-2">
                {inlineNodes(b.text)}
              </p>
            );
        }
      })}
    </div>
  );
}

// ── Export (string HTML, every character escaped) ───────────────────────

export const escHtml = (s: unknown) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

function inlineHtml(s: string): string {
  return parseInline(s)
    .map((n) => {
      switch (n.t) {
        case 'b':
          return `<strong>${escHtml(n.v)}</strong>`;
        case 'i':
          return `<em>${escHtml(n.v)}</em>`;
        case 'code':
          return `<code>${escHtml(n.v)}</code>`;
        case 'a':
          return `<a href="${escHtml(n.href)}" rel="noopener noreferrer">${escHtml(n.v)}</a>`;
        case 'br':
          return '<br>';
        default:
          return escHtml(n.v);
      }
    })
    .join('');
}

export function markdownToHtml(text: string): string {
  return parseBlocks(text)
    .map((b) => {
      switch (b.t) {
        case 'h':
          return `<h${Math.min(6, b.level + 2)}>${inlineHtml(b.text)}</h${Math.min(6, b.level + 2)}>`;
        case 'code':
          return `<pre><code>${escHtml(b.text)}</code></pre>`;
        case 'ul':
        case 'ol':
          return `<${b.t}>${b.items.map((it) => `<li>${inlineHtml(it)}</li>`).join('')}</${b.t}>`;
        case 'quote':
          return `<blockquote>${inlineHtml(b.text)}</blockquote>`;
        case 'table':
          return `<table><thead><tr>${b.head.map((h) => `<th>${inlineHtml(h)}</th>`).join('')}</tr></thead><tbody>${b.rows
            .map((r) => `<tr>${r.map((c) => `<td>${inlineHtml(c)}</td>`).join('')}</tr>`)
            .join('')}</tbody></table>`;
        default:
          return `<p>${inlineHtml(b.text)}</p>`;
      }
    })
    .join('\n');
}
